import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { MdContentCopy, MdLink, MdLinkOff } from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import { usePermission } from '@/lib/hooks/use-permission'
import {
    listShares,
    revokeShare,
    type StorageShare,
} from '@/lib/api/storage/storage-api'
import { formatDateTime } from './storage-utils'

export default function StorageSharesPage() {
    const { canDelete } = usePermission()
    const [shares, setShares] = useState<StorageShare[]>([])
    const [loading, setLoading] = useState(true)
    const [confirmRevoke, setConfirmRevoke] = useState<StorageShare | null>(null)
    const [revoking, setRevoking] = useState(false)

    const load = useCallback(async () => {
        setLoading(true)
        const response = await listShares()
        setShares(response.success ? (response.result ?? []) : [])
        setLoading(false)
    }, [])

    useEffect(() => {
        void load()
    }, [load])

    const handleCopy = async (share: StorageShare) => {
        await navigator.clipboard.writeText(
            `${window.location.origin}/compartilhado/${share.token}`,
        )
        toast.success('Link copiado')
    }

    const handleRevoke = async () => {
        if (!confirmRevoke) return
        setRevoking(true)
        const response = await revokeShare(confirmRevoke.id)
        setRevoking(false)

        if (!response.success) {
            toast.error(response.error || 'Não foi possível revogar o link')
            return
        }

        toast.success('Link revogado')
        setConfirmRevoke(null)
        void load()
    }

    const statusOf = (share: StorageShare) => {
        if (share.expiresAt && new Date(share.expiresAt) < new Date()) {
            return { label: 'Expirado', className: 'badge-warning' }
        }
        if (share.maxDownloads != null && share.downloadCount >= share.maxDownloads) {
            return { label: 'Limite atingido', className: 'badge-warning' }
        }
        return { label: 'Ativo', className: 'badge-success' }
    }

    return (
        <div className="p-4 sm:p-6 space-y-5">
            <div>
                <h1 className="text-2xl font-semibold">Compartilhamentos</h1>
                <p className="text-sm text-base-content/60 mt-0.5">
                    Links públicos ativos dos arquivos da organização
                </p>
            </div>

            {loading ? (
                <div className="flex justify-center py-16">
                    <span className="loading loading-spinner loading-lg text-primary" />
                </div>
            ) : !shares.length ? (
                <div className="text-center py-16">
                    <MdLink className="text-6xl mx-auto text-base-content/20 mb-3" />
                    <p className="font-medium">Nenhum link público ativo</p>
                    <p className="text-sm text-base-content/60 mt-1">
                        Gere links a partir de um arquivo em Armazenamento
                    </p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                <th>Arquivo</th>
                                <th className="w-32">Situação</th>
                                <th className="w-32">Downloads</th>
                                <th className="w-44">Expira em</th>
                                <th className="w-44">Criado em</th>
                                <th className="w-32 text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {shares.map(share => {
                                const status = statusOf(share)
                                return (
                                    <tr key={share.id} className="hover">
                                        <td>
                                            <span className="font-mono text-xs">
                                                {share.fileId
                                                    ? `Arquivo #${share.fileId}`
                                                    : `Pasta #${share.folderId}`}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`badge badge-sm ${status.className}`}>
                                                {status.label}
                                            </span>
                                        </td>
                                        <td className="text-base-content/70">
                                            {share.downloadCount}
                                            {share.maxDownloads != null
                                                ? ` de ${share.maxDownloads}`
                                                : ''}
                                        </td>
                                        <td className="text-base-content/60">
                                            {share.expiresAt
                                                ? formatDateTime(share.expiresAt)
                                                : 'Sem expiração'}
                                        </td>
                                        <td className="text-base-content/60">
                                            {formatDateTime(share.createdAt)}
                                        </td>
                                        <td className="text-right">
                                            <div className="flex justify-end gap-1">
                                                <button
                                                    type="button"
                                                    className="btn btn-xs btn-ghost"
                                                    onClick={() => void handleCopy(share)}
                                                    title="Copiar link"
                                                >
                                                    <MdContentCopy />
                                                </button>
                                                {canDelete('storage', 'shares') && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-xs btn-ghost text-error"
                                                        onClick={() => setConfirmRevoke(share)}
                                                        title="Revogar"
                                                    >
                                                        <MdLinkOff />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <ConfirmModal
                isOpen={Boolean(confirmRevoke)}
                onClose={() => setConfirmRevoke(null)}
                onConfirm={handleRevoke}
                loading={revoking}
                title="Revogar link"
                message="Quem tiver este link deixará de conseguir acessar o arquivo. Esta ação não pode ser desfeita."
                confirmButtonText="Revogar"
                confirmButtonClass="btn-error"
            />
        </div>
    )
}
